import { createContext, ReactNode, useContext, useMemo, useRef, useState } from 'react';
import { PartialLocation, QueryParamAdapter, QueryParamAdapterComponent, QueryParamProvider } from 'use-query-params';

type Props = {
  /** Params the scope opens with, as a search string (`season=winter&in-stock=1`). */
  initialSearch?: string;
  children: ReactNode;
};

/**
 * Keeps every URL-state hook below it -- `useNoddiQueryParam`, table filters, search, sort and
 * pagination -- in local state instead of the address bar, so a list built for a page also works
 * inside a dialog without writing its params onto the page beneath. Unmounting drops the state.
 *
 * use-query-params applies each write on top of the adapter's current location, so the page must not
 * write its own params in the same tick as the scope. A modal guarantees that: the page under it is inert.
 */
export const NavioLocalQueryParamProvider = ({ initialSearch = '', children }: Props) => {
  const [search, setSearch] = useState(initialSearch);
  // State only updates on the next render, so a second write in the same tick (the filter sidebar's
  // exclude + include) would start from the stale search and drop the first.
  const latestSearch = useRef(initialSearch);
  const adapter = useMemo<QueryParamAdapter>(() => {
    const update = (location: PartialLocation) => {
      latestSearch.current = location.search ?? '';
      setSearch(latestSearch.current);
    };
    return {
      get location() {
        return { search: latestSearch.current };
      },
      replace: update,
      push: update
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a new adapter per search re-renders the scope's hooks; the adapter itself reads the ref
  }, [search]);

  return (
    <LocalAdapterContext.Provider value={adapter}>
      <QueryParamProvider adapter={LocalAdapter}>{children}</QueryParamProvider>
    </LocalAdapterContext.Provider>
  );
};

const LocalAdapterContext = createContext<QueryParamAdapter | null>(null);

// The provider takes an adapter component, and a new component per render would remount the scope,
// so this one stays fixed and reads the current adapter from context.
const LocalAdapter: QueryParamAdapterComponent = ({ children }) => {
  const adapter = useContext(LocalAdapterContext);
  return adapter && children(adapter);
};
